import { useState, useEffect } from 'react';
import foodData, { categories } from './foodData';
import { createItem } from '../api/figApi';
import { buildLogRow, resolveGrams, nutrientsForGrams, findEntry } from './foodEntry';
import { getLogCounts, recordLog, topFoodIds } from './foodRecents';
import './Food.css';

// AddFoodSheet — the Phase 5 write path. Two screens in one overlay: PICK a
// food, then CONFIRM the amount.
//
// getToken arrives as a PROP, not from useMsal. Same shape as
// SwimmingHistory.js:11 and ClaimModal.js:14 — the two cleanest existing
// createItem call sites both do it this way, and it keeps this component
// renderable in a test without an MSAL provider.
//
// Chrome is Food-local. CockpitModal at components/FIG/CockpitModal.js is the
// only generic modal in the codebase, but it is a FIG module carrying
// FIG-cpe-* class names styled from FIG.css, and importing it here would pull
// insurance-module styling into Body. The behaviours worth having from it — ESC
// to close, backdrop-click via e.target === e.currentTarget, body scroll lock
// with restore, and `if (!open) return null` — are reimplemented below.
//
// After a successful log the sheet returns to the picker rather than closing.
// Logging a meal is usually several items, and closing would mean reopening and
// losing the recents ordering that the previous save just updated.

const RECENTS_SHOWN = 6;

// Local wall clock as zero-padded 'HH:MM' — the exact contract Food_Log.Time
// declares and toTimeOnly enforces. Not toISOString(), which emits an instant
// in UTC and would log an evening meal at tomorrow's hour. Not
// toLocaleTimeString, which returns '6:15 PM' in this locale and would throw at
// the guard.
function nowHHMM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// Salt is not a food you portion, it is a ratio to the food you just weighed —
// wings get 3% of cooked weight, most other things 2%. Logged as its own row
// rather than folded into the food's numbers, because the food's per100g values
// describe the food and nothing else; adding sodium to them would corrupt a
// snapshot that is never recomputed.
//
// The rows are INDEPENDENT and share only a timestamp. No parent/child field,
// no id reference. What ties them together for a human reading the log later is
// the Notes string, '3% of Chicken Wings (party, fried in tallow)' — which is
// why a salt row without it would be uninterpretable.
const SALTS = [
  { id: 'salt-iodized', label: 'Iodized' },
  { id: 'salt-kosher', label: 'Kosher' },
];
const SALT_IDS = new Set(SALTS.map(s => s.id));
const SALT_PCT_MAX = 10;

function FoodOption({ entry, onPick }) {
  return (
    <button type="button" className="Food-option" onClick={() => onPick(entry)}>
      <span className="Food-option-name">{entry.name}</span>
      <span className="Food-option-meta">{entry.defaultQty} {entry.defaultUnit}</span>
    </button>
  );
}

export default function AddFoodSheet({ open, dateStr, getToken, onClose, onLogged }) {
  // Picker state
  const [search, setSearch] = useState('');
  const [counts, setCounts] = useState({});
  const [selected, setSelected] = useState(null);

  // Confirm state — all of it reset by selectEntry, never carried between foods
  const [qty, setQty] = useState('');
  const [unit, setUnit] = useState('');
  const [count, setCount] = useState('');
  const [qtyTouched, setQtyTouched] = useState(false);
  const [time, setTime] = useState('');
  // Percentage of the resolved food weight, per salt. Keyed by catalog id so
  // the two are genuinely independent — both can be non-zero at once.
  const [saltPct, setSaltPct] = useState({});

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Every hook runs before the `!open` bail-out below, so the hook order is
  // stable across an open/close cycle.
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) { if (e.key === 'Escape') onClose && onClose(); }
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // Counts land in state on OPEN rather than being read inline at render the
  // way RecipeBrowser does. This component stays mounted across several logs,
  // so a value captured once would be stale by the second item — see the
  // departure note at the foot of foodRecents.js. handleLog refreshes it again
  // after each save so the recents row reorders while the sheet is still up.
  useEffect(() => {
    if (!open) return;
    setCounts(getLogCounts());
    setSearch('');
    setSelected(null);
    setSaltPct({});
    setError(null);
  }, [open]);

  if (!open) return null;

  function selectEntry(entry) {
    setSelected(entry);
    setQty(String(entry.defaultQty));
    setUnit(entry.defaultUnit);
    setCount('');
    setQtyTouched(false);
    setTime(nowHHMM());
    // Percentages belong to the food they were set against. Carrying 3% from
    // wings onto the next item would silently salt it.
    setSaltPct({});
    setError(null);
  }

  function bumpSalt(id, delta) {
    setSaltPct(prev => {
      const next = Math.min(SALT_PCT_MAX, Math.max(0, (prev[id] || 0) + delta));
      return { ...prev, [id]: next };
    });
  }

  // Changing the unit invalidates the quantity. '3' meant three wings a moment
  // ago and would mean three grams now — a ~70x error that nothing on screen
  // would flag, because 3g is a perfectly plausible number and the preview
  // would render it without complaint. The number is therefore not carried
  // across a unit change at all.
  //
  // Back to the catalog default when the unit returns to defaultUnit, since
  // that pairing is the one the catalog actually vouches for. Otherwise blank,
  // with the unit name as placeholder so the empty box states what it wants.
  // qtyTouched resets with it: a value this function wrote is the catalog's
  // guess again, not something the user typed. count clears because a piece
  // count is only meaningful for the unit it was entered against.
  function handleUnitChange(nextUnit) {
    setUnit(nextUnit);
    setQty(nextUnit === selected.defaultUnit ? String(selected.defaultQty) : '');
    setQtyTouched(false);
    setCount('');
  }

  async function handleLog() {
    setSaving(true);
    setError(null);
    // Tracks whether the food row made it. Everything after that point is a
    // partial success, not a failure, and must not be reported as one.
    let foodLogged = false;
    try {
      // Throws before any network call on a missing count, an impossible
      // weight, or a bad time — those surface inline instead of as a failed
      // POST the user cannot interpret.
      const fields = buildLogRow({
        entry: selected,
        dateStr,
        time,
        qty: Number(qty),
        unit,
        count: count === '' ? undefined : Number(count),
        qtyTouched,
      });
      const token = await getToken();
      const created = await createItem(token, 'Food_Log', fields);
      foodLogged = true;
      recordLog(selected.id);
      setCounts(getLogCounts());
      onLogged(created);

      // Salt rows, one per non-zero percentage, off the SAME resolved grams the
      // food row was built from and stamped with the SAME time string. Written
      // after the food row on purpose: if the connection dies mid-sequence the
      // meal is still recorded and only the seasoning is missing, which is the
      // recoverable half.
      for (const salt of SALTS) {
        const pct = saltPct[salt.id] || 0;
        if (pct <= 0) continue;
        const saltRow = {
          ...buildLogRow({
            entry: findEntry(salt.id),
            dateStr,
            time,
            qty: fields.Grams * pct / 100,
            unit: 'g',
            count: undefined,
            // Derived from a weight that was measured, not a default that was
            // accepted — so this is a Catalog row, not an eyeball estimate.
            qtyTouched: true,
          }),
          // The only thread back to the food row. Two rows sharing a timestamp
          // is the entire relationship; this is what makes it legible.
          Notes: `${pct}% of ${selected.name}`,
        };
        const saltCreated = await createItem(token, 'Food_Log', saltRow);
        onLogged(saltCreated);
      }

      // Back to the picker with the selection cleared, ready for the next item.
      setSelected(null);
      setSearch('');
    } catch (err) {
      if (foodLogged) {
        // The food row is already on the server. Retrying the whole press would
        // double-log it, so this returns to the picker rather than leaving the
        // Log button armed under a message that looks like nothing happened.
        // The salt is recoverable by logging it by hand.
        setError(`Food logged. Salt row failed: ${err.message}`);
        setSelected(null);
        setSearch('');
      } else {
        setError(err.message);
      }
    } finally {
      setSaving(false);
    }
  }

  const searching = search.trim() !== '';
  const needle = search.trim().toLowerCase();
  const filtered = searching
    ? foodData.filter(e => e.name.toLowerCase().includes(needle))
    : [];
  const recentEntries = topFoodIds(counts, RECENTS_SHOWN).map(findEntry).filter(Boolean);

  const unitDef = selected ? selected.units.find(u => u.unit === unit) : null;

  // An empty quantity is the expected state right after a unit change, not a
  // mistake to shout about. It disables LOG rather than producing an error:
  // Number('') is 0, resolveGrams throws on qty <= 0, and "quantity must be a
  // finite number greater than 0" is a strange thing to say to someone who has
  // not typed anything yet. A missing piece count still goes the other way and
  // errors on press, because there the box is visibly asking for something.
  const qtyEmpty = qty.trim() === '';

  // Live preview. resolveGrams throws while the count box is empty on a
  // requiresCount unit, and that is a normal transient state rather than an
  // error worth showing — the preview reads as an em dash until the input is
  // complete, and the LOG button surfaces the real message if pressed anyway.
  let previewGrams = null;
  let previewCal = null;
  if (selected) {
    try {
      previewGrams = resolveGrams(
        selected, unit, Number(qty), count === '' ? undefined : Number(count)
      );
      const n = nutrientsForGrams(selected, previewGrams);
      previewCal = n.Calories === undefined ? null : n.Calories;
    } catch {
      previewGrams = null;
      previewCal = null;
    }
  }

  // Needs a resolved weight to be a percentage of, and must never appear on the
  // salts themselves.
  const showSaltStrip =
    selected !== null && previewGrams !== null && !SALT_IDS.has(selected.id);

  return (
    <div
      className="Food-sheet-overlay"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="Food-sheet" onMouseDown={e => e.stopPropagation()}>

        {selected === null ? (
          <>
            <div className="Food-sheet-header">
              <h2 className="Food-sheet-title">Add Food</h2>
              <button
                type="button"
                className="Food-sheet-close"
                onClick={onClose}
                aria-label="Close"
              >
                &#x2715;
              </button>
            </div>

            <div className="Food-sheet-body">
              {/* Screen A carries the error too, because the partial-failure
                  path lands here: the food row saved, a salt row did not, and
                  the message has to survive the return to the picker or the
                  user is told nothing at all. */}
              {error && <p className="Food-sheet-error">{error}</p>}

              {/* autoFocus is deliberately absent. On a phone it raises the
                  keyboard immediately, which scrolls the recents row off
                  screen — and tapping a recent is the fast path this whole
                  screen exists to serve. */}
              <input
                className="Food-search"
                type="search"
                placeholder="Search foods"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />

              {!searching && recentEntries.length > 0 && (
                <section className="Food-picker-section">
                  <h3 className="Food-picker-heading">Recent</h3>
                  <div className="Food-picker-grid">
                    {recentEntries.map(entry => (
                      <FoodOption key={entry.id} entry={entry} onPick={selectEntry} />
                    ))}
                  </div>
                </section>
              )}

              {searching ? (
                filtered.length === 0 ? (
                  <p className="Food-picker-empty">No food matches &ldquo;{search.trim()}&rdquo;.</p>
                ) : (
                  <div className="Food-picker-grid">
                    {filtered.map(entry => (
                      <FoodOption key={entry.id} entry={entry} onPick={selectEntry} />
                    ))}
                  </div>
                )
              ) : (
                categories.map(cat => {
                  const items = foodData.filter(e => e.category === cat);
                  if (items.length === 0) return null;
                  return (
                    <section className="Food-picker-section" key={cat}>
                      <h3 className="Food-picker-heading">{cat}</h3>
                      <div className="Food-picker-grid">
                        {items.map(entry => (
                          <FoodOption key={entry.id} entry={entry} onPick={selectEntry} />
                        ))}
                      </div>
                    </section>
                  );
                })
              )}
            </div>
          </>
        ) : (
          <>
            <div className="Food-sheet-header">
              <button
                type="button"
                className="Food-sheet-back"
                onClick={() => { setSelected(null); setError(null); }}
                aria-label="Back to food list"
              >
                &lsaquo;
              </button>
              <h2 className="Food-sheet-title">{selected.name}</h2>
              <button
                type="button"
                className="Food-sheet-close"
                onClick={onClose}
                aria-label="Close"
              >
                &#x2715;
              </button>
            </div>

            <div className="Food-sheet-body">
              <label className="Food-field">
                <span className="Food-field-label">Quantity</span>
                <input
                  className="Food-field-input"
                  type="number"
                  inputMode="decimal"
                  value={qty}
                  placeholder={unit}
                  onChange={e => { setQty(e.target.value); setQtyTouched(true); }}
                />
              </label>

              {/* Changing the unit never sets qtyTouched — see
                  handleUnitChange, which rewrites the quantity rather than
                  letting a number written for one unit stand under another. */}
              <div className="Food-field">
                <span className="Food-field-label">Unit</span>
                {selected.units.length === 1 ? (
                  <span className="Food-field-static">{selected.units[0].unit}</span>
                ) : (
                  <select
                    className="Food-field-input"
                    value={unit}
                    onChange={e => handleUnitChange(e.target.value)}
                  >
                    {selected.units.map(u => (
                      <option key={u.unit} value={u.unit}>{u.unit}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Per-UNIT, not per-entry. chicken-wings at 'wing' or 'g' shows
                  no count field; only 'g bone-in' does, because only that unit
                  carries requiresCount.

                  Sits below Quantity deliberately, and says so in its own
                  words. Read as two anonymous number boxes these are easy to
                  swap, and swapping them is silent: 8 in the weight box and 420
                  in the count box both parse. The label names the thing being
                  counted and the helper points back up at the field above it. */}
              {unitDef?.requiresCount === true && (
                <label className="Food-field">
                  <span className="Food-field-label">How many wings on the plate?</span>
                  <span className="Food-field-hint Food-field-hint--block">
                    Weight above is the whole plate. Bones get subtracted.
                  </span>
                  <input
                    className="Food-field-input"
                    type="number"
                    inputMode="numeric"
                    value={count}
                    placeholder="count"
                    onChange={e => setCount(e.target.value)}
                  />
                </label>
              )}

              {/* No step attribute: the default already excludes seconds and
                  reports exactly 'HH:MM'. In a US locale the control presents a
                  12-hour AM/PM picker while still reporting 24-hour — that
                  conversion is the browser's, and nothing here reformats it. */}
              <label className="Food-field">
                <span className="Food-field-label">Time</span>
                <input
                  className="Food-field-input"
                  type="time"
                  value={time}
                  onChange={e => setTime(e.target.value)}
                />
              </label>

              <p className="Food-preview">
                {previewGrams === null ? (
                  <span className="Food-undisclosed" title="Enter a valid amount">&mdash;</span>
                ) : (
                  <>
                    {Math.round(previewGrams)}<span className="Food-unit"> g</span>
                    {previewCal !== null && (
                      <>
                        {' · '}
                        {Math.round(previewCal)}<span className="Food-unit"> cal</span>
                      </>
                    )}
                  </>
                )}
              </p>

              {/* Salt strip. Shown only when a real gram figure exists to take
                  a percentage OF, and never on the salts themselves — a
                  percentage of salt in salt is meaningless and would let two
                  rows be written for the same thing. */}
              {showSaltStrip && (
                <div className="Food-salt">
                  <span className="Food-salt-heading">Salt</span>
                  {SALTS.map(salt => {
                    const pct = saltPct[salt.id] || 0;
                    const grams = previewGrams * pct / 100;
                    return (
                      <div className="Food-salt-row" key={salt.id}>
                        <span className="Food-salt-label">{salt.label}</span>
                        <span className="Food-salt-stepper">
                          <button
                            type="button"
                            className="Food-salt-btn"
                            onClick={() => bumpSalt(salt.id, -1)}
                            disabled={pct === 0}
                            aria-label={`Less ${salt.label} salt`}
                          >
                            &minus;
                          </button>
                          <span className="Food-salt-pct">{pct}%</span>
                          <button
                            type="button"
                            className="Food-salt-btn"
                            onClick={() => bumpSalt(salt.id, 1)}
                            disabled={pct === SALT_PCT_MAX}
                            aria-label={`More ${salt.label} salt`}
                          >
                            +
                          </button>
                        </span>
                        <span className="Food-salt-grams">({grams.toFixed(1)} g)</span>
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                type="button"
                className="Food-log-btn"
                onClick={handleLog}
                disabled={saving || qtyEmpty}
              >
                {saving ? 'Logging…' : 'Log'}
              </button>

              {error && <p className="Food-sheet-error">{error}</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
